/**
 * Utility functions for Facebook Ads conversion tracking
 */

/**
 * Extract fbclid from URL search parameters
 */
export function getFbclidFromUrl(url?: string): string | null {
  try {
    const urlToCheck = url || (typeof window !== 'undefined' ? window.location.href : '');
    const urlObj = new URL(urlToCheck);
    return urlObj.searchParams.get('fbclid');
  } catch (error) {
    console.warn('Error extracting fbclid from URL:', error);
    return null;
  }
}

/**
 * Convert fbclid to fbc format as required by Meta Conversion API
 * Format: fb.{subdomainIndex}.{creationTime}.{fbclid}
 */
export function convertFbclidToFbc(fbclid: string): string {
  if (!fbclid) {
    throw new Error('fbclid is required');
  }

  // Get current timestamp in seconds
  const creationTime = Math.floor(Date.now() / 1000);

  // Subdomain index - use 1 as default for main domain
  const subdomainIndex = 1;

  return `fb.${subdomainIndex}.${creationTime}.${fbclid}`;
}

/**
 * Get user agent string for Conversion API
 */
export function getUserAgent(): string {
  if (typeof window === 'undefined') return '';
  return window.navigator.userAgent;
}

/**
 * Store fbclid in sessionStorage for persistence across the signup flow
 */
export function storeFbclid(fbclid: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem('curipod_fbclid', fbclid);
  } catch (error) {
    console.warn('Error storing fbclid:', error);
  }
}

/**
 * Retrieve fbclid from sessionStorage
 */
export function getStoredFbclid(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem('curipod_fbclid');
  } catch (error) {
    console.warn('Error retrieving stored fbclid:', error);
    return null;
  }
}

/**
 * Get fbclid from URL or storage, with preference for URL
 */
export function getFbclid(): string | null {
  const urlFbclid = getFbclidFromUrl();
  if (urlFbclid) {
    // Store it for later use
    storeFbclid(urlFbclid);
    return urlFbclid;
  }

  // Fallback to stored value
  return getStoredFbclid();
}
